<script setup lang="ts">
import dayjs from 'dayjs'
import { pluginKeys } from '@/plugins/dayjs'
import Btn from '@/components/atoms/Btn/index.vue'
import Datepicker from '@/components/atoms/Datepicker/index.vue'
import { useVueInject } from '@/hooks/vue'
import { ISearchPhotographerProps } from '@/types/common/photographer'
import { findColorValue } from '@/utils/style'
import { getGroupedPrefs } from '@/utils/options/prefectures'
import { useStates, useActions } from '.'

const injectedDayjs = useVueInject<typeof dayjs>(pluginKeys.DAYJS)

interface IProps {
  holidays: ISearchPhotographerProps['holidays']
  pref: ISearchPhotographerProps['pref']
  prefVal?: ISearchPhotographerProps['prefVal']
  categoryVal?: ISearchPhotographerProps['categoryVal']
  color?: ISearchPhotographerProps['color']
}

const props = withDefaults(defineProps<IProps>(), {
  prefVal: '',
  categoryVal: '',
  color: 'light-cream',
})
const states = useStates(props)
const actions = useActions(props, states)

const now = injectedDayjs().tz()
const minDate = now.add(1, 'day').format('YYYY-MM-DD')
const maxDate = now.add(365, 'day').format('YYYY-MM-DD')
</script>

<template>
  <div class="search-photographer">
    <slot />
    <div class="search-photographer-form">
      <div class="search-photographer-form-date">
        <Datepicker
          v-model="states.dateVal"
          :holidays="props.holidays"
          :min-date="minDate"
          :max-date="maxDate"
          :start-date="minDate"
          :input-styles="{ border: 'none', height: '100%' }"
        ></Datepicker>
      </div>
      <div class="search-photographer-form-pref">
        <select v-model="states.prefVal">
          <option value="">都道府県を選択</option>
          <optgroup v-for="(prefs, region, index) in getGroupedPrefs(props.pref)" :key="index" :label="String(region)">
            <option v-for="prefItem in prefs" :key="prefItem.id" :value="prefItem.id">{{ prefItem.pref }}</option>
          </optgroup>
        </select>
      </div>
      <Btn
        class="search-photographer-form-button"
        tag="button"
        type="button"
        color="red"
        aria-label="フォトグラファーを探す"
        @click="actions.search()"
      ></Btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/styles/mixins/form' as form-mixins;
.search-photographer {
  background-color: v-bind('findColorValue(props.color)');
  text-align: center;
  padding: 30px 10px;
  .search-photographer-form {
    display: inline-flex;
    gap: 8px;
    width: 600px;
    max-width: 100%;
    height: 40px;
    @media (max-width: 767px) {
      width: 100%;
    }
    .search-photographer-form-date,
    .search-photographer-form-pref {
      flex: 1 1 0;
    }
    .search-photographer-form-pref > select {
      @include form-mixins.form-input;
      border: none;
      background-image: url(/assets/images/arrow-bottom-icon.svg);
      background-position: right 10px center;
      background-size: 12px 12px;
      background-repeat: no-repeat;
      height: 100%;
      line-height: 1;
      cursor: pointer;
    }
    .search-photographer-form-button {
      width: 45px;
      height: auto;
      background: url(/assets/images/iconmonstr-magnifier-3-icon-white.svg) center / 24px no-repeat;
    }
  }
}
</style>
